import {
    Alert,
    Anchor,
    Button,
    Group,
    HoverCard,
    Loader,
    Stack,
    Text,
    UnstyledButton,
} from '@mantine/core'
import {Link} from '@tanstack/react-router'
import {type ReactNode, useState} from 'react'

import {AssignmentStrip} from 'src/assignments/assignment-strip'
import {JOB_PEEK_ASSIGNMENT_STRIP_DATE_RANGE} from 'src/assignments/assignment-strip-utils'

import {useRentalAssetAssignments} from './use-rental-asset-assignments'

type RentalAssetAssignmentPeekProps = {
    assetId: string
    assetName: string
    assetTypeName: string
    referenceDate: Date
    children: ReactNode
}

export function RentalAssetAssignmentPeek({
    assetId,
    assetName,
    assetTypeName,
    referenceDate,
    children,
}: RentalAssetAssignmentPeekProps) {
    const [fetchEnabled, setFetchEnabled] = useState(false)
    const {
        data: assignmentList,
        isLoading,
        isError,
        error,
        refetch,
        referenceDate: resolvedReferenceDate,
        dateRange,
    } = useRentalAssetAssignments(assetId, {
        enabled: fetchEnabled,
        referenceDate,
        dateRange: JOB_PEEK_ASSIGNMENT_STRIP_DATE_RANGE,
    })

    return (
        <HoverCard
            width={700}
            shadow="md"
            withArrow
            openDelay={200}
            closeDelay={100}
            onOpen={() => setFetchEnabled(true)}
            onClose={() => setFetchEnabled(false)}
        >
            <HoverCard.Target>
                <UnstyledButton style={{display: 'block', minWidth: 0, flex: 1}}>
                    {children}
                </UnstyledButton>
            </HoverCard.Target>
            <HoverCard.Dropdown p="md">
                <Stack gap="md">
                    <Group justify="space-between" align="flex-start" wrap="nowrap">
                        <Stack gap={2} style={{minWidth: 0}}>
                            <Text fw={600} truncate>
                                {assetName}
                            </Text>
                            <Text size="sm" c="dimmed" truncate>
                                {assetTypeName}
                            </Text>
                        </Stack>
                        <Anchor
                            component={Link}
                            to="/rental-assets/$assetId"
                            params={{assetId}}
                            size="sm"
                            style={{flexShrink: 0}}
                        >
                            View full details
                        </Anchor>
                    </Group>

                    {isLoading && (
                        <Group justify="center" py="sm">
                            <Loader size="sm" />
                        </Group>
                    )}

                    {isError && (
                        <Alert color="red" title="Could not load assignments">
                            {error instanceof Error ? error.message : 'Something went wrong.'}
                            <Button
                                onClick={() => void refetch()}
                                variant="light"
                                size="compact-sm"
                                mt="sm"
                            >
                                Retry
                            </Button>
                        </Alert>
                    )}

                    {assignmentList && !isLoading && !isError && (
                        <AssignmentStrip
                            assignments={assignmentList.assignments}
                            referenceDate={resolvedReferenceDate}
                            referenceDayLabel="Start"
                            dayInteraction="none"
                            dateRange={dateRange}
                            presentation="schedule"
                            showTitle={false}
                            showLegend={false}
                        />
                    )}
                </Stack>
            </HoverCard.Dropdown>
        </HoverCard>
    )
}
